import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { useApi } from "@/core/api";
import {
  IServiceTitanCredential,
  ServiceTitanCredentialParams,
  IBusiness,
} from "./business";

export const serviceTitanCredentialsKey = (businessId?: string | null) =>
  ["business", businessId, "service-titan-credentials"] as const;

/**
 * Hook to fetch ServiceTitan credentials for a business
 * @param businessId The business ID
 * @returns Query result with ServiceTitan credentials
 */
export const useGetServiceTitanCredentials = (businessId?: string | null) => {
  const api = useApi();

  return useQuery({
    queryKey: serviceTitanCredentialsKey(businessId),
    queryFn: (): Promise<IServiceTitanCredential> =>
      api
        .get(`/businesses/${businessId}/service_titan_credential`)
        .then((response) => response.data),
    retry: false,
    enabled: !!businessId,
  });
};

/**
 * Hook to create ServiceTitan credentials for a business
 * @param businessId The business ID
 * @returns Mutation function for creating ServiceTitan credentials
 */
export const useCreateServiceTitanCredentials = (businessId: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      params: ServiceTitanCredentialParams,
    ): Promise<AxiosResponse<IServiceTitanCredential>> =>
      api.post(`/businesses/${businessId}/service_titan_credential`, params),
    onSuccess: (response) => {
      queryClient.setQueryData(
        serviceTitanCredentialsKey(businessId),
        response.data,
      );

      // Update the business to indicate ServiceTitan is enabled
      queryClient.setQueryData(["business", businessId], (old: IBusiness) => {
        if (!old) return old;
        return {
          ...old,
          service_titan_enabled: true,
        };
      });
    },
  });
};

/**
 * Hook to update ServiceTitan credentials for a business
 * @param businessId The business ID
 * @returns Mutation function for updating ServiceTitan credentials
 */
export const useUpdateServiceTitanCredentials = (businessId: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      params: ServiceTitanCredentialParams,
    ): Promise<AxiosResponse<IServiceTitanCredential>> =>
      api.put(`/businesses/${businessId}/service_titan_credential`, params),
    onSuccess: (response) => {
      queryClient.setQueryData(
        serviceTitanCredentialsKey(businessId),
        response.data,
      );
    },
  });
};

/**
 * Hook to upsert ServiceTitan credentials for a business
 * @param businessId The business ID
 * @returns Mutation function for upserting ServiceTitan credentials
 */
export const useUpsertServiceTitanCredentials = (businessId: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      params: ServiceTitanCredentialParams & { id?: string },
    ): Promise<AxiosResponse<IServiceTitanCredential>> =>
      params.id
        ? api.put(`/businesses/${businessId}/service_titan_credential`, params)
        : api.post(
            `/businesses/${businessId}/service_titan_credential`,
            params,
          ),
    onSuccess: (response) => {
      queryClient.setQueryData(
        serviceTitanCredentialsKey(businessId),
        response.data,
      );

      // Update the business to indicate ServiceTitan is enabled
      queryClient.setQueryData(["business", businessId], (old: IBusiness) => {
        if (!old) return old;
        return {
          ...old,
          service_titan_enabled: true,
        };
      });
    },
  });
};
